import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/axios';
import type { AgentInstaller, AgentOs, TenantInstaller } from '@/types/agentUpdate';

// The agent-update endpoints are guarded by a static shared key rather than the
// admin JWT, so every call here sends it as X-Api-Key.
const API_KEY_HEADERS = { 'X-Api-Key': import.meta.env.VITE_AGENT_UPDATE_API_KEY ?? '' };

export const installerKeys = {
  all: ['agent-installers'] as const,
};

export function useInstallers() {
  return useQuery({
    queryKey: installerKeys.all,
    queryFn: async () => {
      const { data } = await apiClient.get<AgentInstaller[]>('/agent-update/versions', {
        headers: API_KEY_HEADERS,
      });
      return data;
    },
  });
}

export interface UploadInstallerInput {
  os: AgentOs;
  version: string;
  file: File;
  onProgress?: (percent: number) => void;
}

// meta.suppressErrorToast: the upload forms render the error inline.
export function useUploadInstaller() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ os, version, file, onProgress }: UploadInstallerInput) => {
      const form = new FormData();
      // Text fields go before the file so the server sees them first while streaming.
      form.append('os', os);
      form.append('version', version);
      form.append('file', file);

      const { data } = await apiClient.post<AgentInstaller>('/agent-update/upload', form, {
        headers: { ...API_KEY_HEADERS, 'Content-Type': 'multipart/form-data' },
        timeout: 0, // installers are large; the default 30s would abort the transfer
        onUploadProgress: (e) => {
          if (e.total) onProgress?.(Math.round((e.loaded / e.total) * 100));
        },
      });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: installerKeys.all }),
    meta: { suppressErrorToast: true },
  });
}

export interface UploadTenantInstallerInput extends UploadInstallerInput {
  tenantId: string;
  /** The tenant console's ingest API key, typed by the admin per upload. */
  apiKey: string;
}

/** Forwarded by this server to the tenant's own console (JWT-authenticated; no API key needed in the browser). */
export function useUploadTenantInstaller() {
  return useMutation({
    mutationFn: async ({ tenantId, apiKey, os, version, file, onProgress }: UploadTenantInstallerInput) => {
      const form = new FormData();
      form.append('os', os);
      form.append('version', version);
      form.append('apiKey', apiKey);
      form.append('file', file);

      const { data } = await apiClient.post(`/tenants/${encodeURIComponent(tenantId)}/agent-update/upload`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 0,
        onUploadProgress: (e) => {
          if (e.total) onProgress?.(Math.round((e.loaded / e.total) * 100));
        },
      });
      return data;
    },
    meta: { suppressErrorToast: true },
  });
}

// The tenant console's ingest key travels in a header (never a URL) and is only
// held in component state by the caller — it is not stored anywhere.
export interface TenantConsoleTarget {
  tenantId: string;
  apiKey: string;
}

const tenantInstallerKeys = {
  list: (tenantId: string) => ['tenant-installers', tenantId] as const,
};

const tenantHeaders = (apiKey: string) => ({ 'X-Tenant-Api-Key': apiKey });
const tenantVersionPath = (tenantId: string, os: AgentOs, version: string) =>
  `/tenants/${encodeURIComponent(tenantId)}/agent-update/versions/${encodeURIComponent(os)}/${encodeURIComponent(version)}`;

/** Installers stored on the tenant's console; stays idle until `target` is set. */
export function useTenantInstallers(target: TenantConsoleTarget | null) {
  return useQuery({
    queryKey: tenantInstallerKeys.list(target?.tenantId ?? ''),
    enabled: target !== null,
    retry: false,
    queryFn: async () => {
      const { data } = await apiClient.get<TenantInstaller[]>(
        `/tenants/${encodeURIComponent(target!.tenantId)}/agent-update/versions`,
        { headers: tenantHeaders(target!.apiKey) },
      );
      return data;
    },
  });
}

export function useInvalidateTenantInstallers() {
  const qc = useQueryClient();
  return (tenantId: string) => qc.invalidateQueries({ queryKey: tenantInstallerKeys.list(tenantId) });
}

export function useDeleteTenantInstaller() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ tenantId, apiKey, os, version }: TenantConsoleTarget & { os: AgentOs; version: string }) => {
      const { data } = await apiClient.delete(tenantVersionPath(tenantId, os, version), {
        headers: tenantHeaders(apiKey),
      });
      return data;
    },
    onSuccess: (_data, { tenantId }) => qc.invalidateQueries({ queryKey: tenantInstallerKeys.list(tenantId) }),
    meta: { suppressErrorToast: true },
  });
}

/** A blob response body can't be read as JSON by axios, so pull the server's message out by hand. */
async function blobErrorMessage(err: unknown): Promise<unknown> {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text()) as { message?: string | string[] };
      const message = Array.isArray(parsed.message) ? parsed.message.join(', ') : parsed.message;
      if (message) return new Error(message);
    } catch {
      // not JSON — fall through to the original error
    }
  }
  return err;
}

export async function downloadTenantInstaller(
  { tenantId, apiKey }: TenantConsoleTarget,
  installer: TenantInstaller,
): Promise<void> {
  let data: Blob;
  try {
    ({ data } = await apiClient.get<Blob>(`${tenantVersionPath(tenantId, installer.os, installer.version)}/download`, {
      headers: tenantHeaders(apiKey),
      responseType: 'blob',
      timeout: 0,
    }));
  } catch (err) {
    throw await blobErrorMessage(err);
  }
  const url = URL.createObjectURL(data);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${installer.os}-${installer.version}-${installer.fileName}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function useDeleteInstaller() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/agent-update/versions/${id}`, { headers: API_KEY_HEADERS });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: installerKeys.all }),
  });
}

export async function downloadInstaller(installer: AgentInstaller): Promise<void> {
  const { data } = await apiClient.get<Blob>('/agent-update/download', {
    params: { os: installer.os, version: installer.version },
    headers: API_KEY_HEADERS,
    responseType: 'blob',
    timeout: 0,
  });
  const url = URL.createObjectURL(data);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${installer.os}-${installer.version}-${installer.fileName}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
